import type { FC, ReactNode } from "react";
import { useEffect } from "react";
import { I18nextProvider } from "react-i18next";

import { AviaryProvider } from "@aviary/utils";
import { BannersProvider, LoadingProvider, Router } from "@shared/components";
import type { ServerData } from "@shared/context";
import {
  GlobalContextProvider as SharedGlobalContextProvider,
  GlobalContextUserRole,
  ServerContext,
} from "@shared/context";
import { CAGlobals, USGlobals } from "@shared/data/globals";
import type { OpenCatalogInitialProps } from "@shared/types/openCatalogInitialProps";
import { getInitialProps } from "@shared/utils";
import { FlippersProvider, InitialPropsProvider } from "@unauthenticated/catalog/context";
import { i18n } from "@unauthenticated/catalog/locales/i18n";

import { ApolloClientProvider } from "./ApolloClientProvider";
import { openCatalogBrowserHistory } from "./openCatalogBrowserHistory";

interface Props {
  server: ServerData;
  initialProps: OpenCatalogInitialProps;
  children: ReactNode;
}

const Providers: FC<Props> = ({ children, initialProps, server }) => {
  useEffect(() => {
    i18n.changeLanguage("en");
  }, []);

  const renderLocalProviders = () => {
    const { fullscriptCountry, e2e } = initialProps ?? getInitialProps();

    const getCountry = () => {
      if (fullscriptCountry === "US") return USGlobals.country;

      return CAGlobals.country;
    };

    return (
      <SharedGlobalContextProvider
        country={getCountry()}
        role={GlobalContextUserRole.UNAUTHENTICATED}
        e2e={e2e}
      >
        <BannersProvider>
          <AviaryProvider>{children}</AviaryProvider>
        </BannersProvider>
      </SharedGlobalContextProvider>
    );
  };

  return (
    <ServerContext.Provider value={server}>
      <InitialPropsProvider initialProps={initialProps}>
        <FlippersProvider>
          <I18nextProvider i18n={i18n}>
            <LoadingProvider>
              <ApolloClientProvider>
                <Router history={openCatalogBrowserHistory()}>{renderLocalProviders()}</Router>
              </ApolloClientProvider>
            </LoadingProvider>
          </I18nextProvider>
        </FlippersProvider>
      </InitialPropsProvider>
    </ServerContext.Provider>
  );
};

export { Providers };
